import { BRAND } from '../../config/branding';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Users, Lock } from 'lucide-react';

export default function ResponsibleAIPage() {
  usePageMeta({
    title: 'Responsible AI Practices – Stratel',
    description: 'Read about Stratel’s commitment to ethical and transparent use of AI in healthcare consulting workflows.',
    keywords: 'responsible AI, ethical AI consulting, AI transparency, Stratel AI policies',
  });
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        <div className="bg-white rounded-xl shadow-sm border p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Responsible Use of AI</h1>
              <p className="text-gray-600">at {BRAND.name}</p>
            </div>
          </div>

          <div className="prose prose-gray max-w-none">
            <p className="text-gray-700 text-lg">
              {BRAND.name} uses assistive AI tools to support research, drafting, and internal 
              workflow efficiency. AI-generated outputs are treated as <strong>drafts</strong> and 
              require <strong>human review</strong> before any client-facing use.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
              <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-green-900 m-0">What AI Helps With</h3>
                </div>
                <ul className="text-green-800 space-y-2 m-0 pl-5">
                  <li>Drafting outreach language for internal review</li>
                  <li>Summarizing publicly available information</li>
                  <li>Prioritizing leads using explainable scoring</li>
                  <li>Generating document templates and reports</li>
                  <li>Analyzing patterns in operational data</li>
                </ul>
              </div>

              <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                <div className="flex items-center gap-2 mb-3">
                  <Lock className="w-5 h-5 text-red-600" />
                  <h3 className="font-semibold text-red-900 m-0">What AI Does Not Do</h3>
                </div>
                <ul className="text-red-800 space-y-2 m-0 pl-5">
                  <li>No automated sending of messages</li>
                  <li>No impersonation of individuals</li>
                  <li>No access to private systems</li>
                  <li>No decisions without human approval</li>
                  <li>No use of non-public data sources</li>
                </ul>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 border my-8">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-gray-700" />
                <h3 className="font-semibold text-gray-900 m-0">Human Oversight</h3>
              </div>
              <p className="text-gray-700 m-0">
                Our workflows include multiple safeguards to ensure responsible AI use:
              </p>
              <ul className="text-gray-700 mt-3 space-y-2">
                <li><strong>Approval Gates:</strong> All AI-generated content requires human review before use</li>
                <li><strong>Audit Trails:</strong> We maintain complete records of AI assistance and human decisions</li>
                <li><strong>Access Controls:</strong> AI tools operate within defined boundaries and permissions</li>
                <li><strong>Transparency:</strong> We disclose when AI has assisted in producing deliverables</li>
              </ul>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">Our Commitments</h2>

            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Accuracy Over Speed</h4>
                  <p className="text-gray-600">We prioritize accuracy and quality over rapid AI-generated outputs. Every piece of client-facing work is verified by our consultants.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Privacy First</h4>
                  <p className="text-gray-600">Client data is never used to train AI models. We maintain strict data handling protocols that exceed industry standards.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Continuous Improvement</h4>
                  <p className="text-gray-600">We regularly review and update our AI practices based on emerging best practices and client feedback.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 font-bold">4</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Client Choice</h4>
                  <p className="text-gray-600">We can tailor our workflows to match your organization's preferences regarding AI assistance.</p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 m-0">
                <strong>Questions about our AI practices?</strong> We're happy to discuss our approach in detail. 
                <Link to="/contact" className="text-blue-600 hover:text-blue-800 ml-1">Contact us →</Link>
              </p>
            </div>
          </div>
        </div>

        <p className="text-center text-gray-500 text-sm mt-8">
          Last updated: December 2024
        </p>
      </div>
    </div>
  );
}
